import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Content,
  ButtonsContainer,
  ProductImage,
  ProductInfo,
  Title,
  Description,
  Button,
  ProductImageSectionColor,
  ProductImageSection,
  Options,
  DescriptionProduct,
  ButtonRight,
  ButtonLeft,
  MoreButton,
  ContentContainer
} from "./ContentProduct.style";
import ProductSlider from "../ProductSlider/ProductSlider";
import { VscTriangleLeft, VscTriangleRight } from "react-icons/vsc";
import axios from "axios";

const ContentProduct = () => {
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [categories, setCategories] = useState([]);
  const [currentCategoryIndex, setCurrentCategoryIndex] = useState(0);
  const { productId, lineId, subCategory } = useParams();
  const [content, setContent] = useState("description");
  const navigate = useNavigate();

  const fetchProduct = useCallback(async (id) => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/products/${id}`
      );
      setProduct(response.data);
    } catch (err) {
      setError("Failed to fetch product data");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/lines/${lineId}`);
      const lineCategories = response.data;
      setCategories(lineCategories);
      const categoryIndex = lineCategories.findIndex(cat => cat.id === subCategory);
      setCurrentCategoryIndex(categoryIndex !== -1 ? categoryIndex : 0);
    } catch (err) {
      setError("Failed to fetch category data");
      console.error(err);
    }
  }, [lineId, subCategory]);

  useEffect(() => {
    fetchProduct(productId);
    fetchCategories();
  }, [productId, lineId, subCategory, fetchProduct, fetchCategories]);

const handleProductChange = useCallback((direction) => {
  if (!categories.length) {
    console.error("No categories available");
    return;
  }

  const currentCategory = categories[currentCategoryIndex];
  let newProductId = parseInt(productId);
  let newCategoryIndex = currentCategoryIndex;

  if (direction === 'next') {
    if (newProductId >= currentCategory.lastProductId) {
      // If we're at the last product of the current category
      if (currentCategoryIndex < categories.length - 1) {
        // If there's a next category available
        newCategoryIndex = currentCategoryIndex + 1;
        newProductId = categories[newCategoryIndex].firstProductId;
      } else {
        // If we're at the last category, wrap around to the first category
        newCategoryIndex = 0;
        newProductId = categories[0].firstProductId;
      }
    } else {
      newProductId++;
    }
  } else if (direction === 'prev') {
    if (newProductId <= currentCategory.firstProductId) {
      // If we're at the first product of the current category
      if (currentCategoryIndex > 0) {
        // If there's a previous category available
        newCategoryIndex = currentCategoryIndex - 1;
        newProductId = categories[newCategoryIndex].lastProductId;
      } else {
        // If we're at the first category, wrap around to the last category
        newCategoryIndex = categories.length - 1;
        newProductId = categories[newCategoryIndex].lastProductId;
      }
    } else {
      newProductId--;
    }
  }

  setCurrentCategoryIndex(newCategoryIndex);
  const newCategory = categories[newCategoryIndex].id;
  navigate(`/lineas/${lineId}/${newCategory}/${newProductId}`);
  fetchProduct(newProductId);
}, [categories, currentCategoryIndex, productId, lineId, navigate, fetchProduct]);

const isFirstProduct = useCallback(() => {
  if (!categories.length) return true;
  const currentCategory = categories[currentCategoryIndex];
  return parseInt(productId) <= currentCategory.firstProductId;
}, [categories, currentCategoryIndex, productId]);

const isLastProduct = useCallback(() => {
  if (!categories.length) return true;
  const currentCategory = categories[currentCategoryIndex];
  return parseInt(productId) >= currentCategory.lastProductId;
}, [categories, currentCategoryIndex, productId]);

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;
  if (!product) return <div>No product found</div>;

  return (
    <Content id="content-product">
      <ProductImage>
        <ProductImageSection>
          <img src={product.imageUrl} alt={product.name} />
          <div></div>
        </ProductImageSection>
        <ProductImageSectionColor brand={lineId} category={subCategory} />
      </ProductImage>
      <ProductInfo>
        {/* <ProductSlider /> */}
        <ContentContainer>
          <Title brand={lineId} category={subCategory}>{product.name}</Title>
          <Description>
            <i>{product.shortDescription}</i>
          </Description>
          <Options brand={lineId} category={subCategory}>
            <p 
              onClick={() => setContent("description")} 
              className={content === "description" ? "selected" : ""}
            >
              Indicaciones
            </p>
            <p 
              onClick={() => setContent("presentation")} 
              className={content === "presentation" ? "selected" : ""}
            >
              Presentación
            </p>
            <p 
              onClick={() => setContent("components")} 
              className={content === "components" ? "selected" : ""}
            >
              Componentes
            </p>
          </Options>
          <DescriptionProduct key={content}>
            {content === "description" && (
              <div dangerouslySetInnerHTML={{ __html: product.description }} />
            )}
            {content === "presentation" && (
              <div dangerouslySetInnerHTML={{ __html: product.presentation }} />
            )}
            {content === "components" && (
              <div dangerouslySetInnerHTML={{ __html: product.components }} />
            )}
          </DescriptionProduct>
        </ContentContainer>
        <MoreButton>
          <ButtonsContainer>
            <ButtonLeft
              onClick={() => !isFirstProduct() && handleProductChange('prev')}
              style={{ opacity: isFirstProduct() ? 0.5 : 1, cursor: isFirstProduct() ? 'not-allowed' : 'pointer' }}
            >
              <VscTriangleLeft />
            </ButtonLeft>
            <ButtonRight
              onClick={() => !isLastProduct() && handleProductChange('next')}
              style={{ opacity: isLastProduct() ? 0.5 : 1, cursor: isLastProduct() ? 'not-allowed' : 'pointer' }}
            >
              <VscTriangleRight />
            </ButtonRight>
          </ButtonsContainer>
          <Button>Ver Vademécum</Button>
        </MoreButton>
      </ProductInfo>
    </Content>
  );
};

export default ContentProduct;
